import { Box, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Info } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
    LabelText,
    LabelValueBox,
    StyledIdField,
    ValueText,
} from '~/components/Fields/TooltipLabeledValue';
import { DoseData, doseScaleState } from '~/state/atoms/dose/doseAtoms';

export function DoseInfo({ dose }: { dose: DoseData }) {
    const doseScale = useRecoilValue(doseScaleState);
    const doseScaleDisplay = doseScale !== 1 ? doseScale.toFixed(2) : null;
    const doseGridResolution = dose.doseGridResolution
        ? `${dose.doseGridResolution[0]} x ${dose.doseGridResolution[1]} x ${dose.doseGridResolution[2]}`
        : null;
    return (
        <Tooltip
            title={
                <Box whiteSpace="nowrap">
                    <LabelValueBox>
                        <LabelText>Dose Type:</LabelText>
                        <ValueText>
                            {dose.generated ? (
                                <Typography
                                    component="span"
                                    textTransform="uppercase"
                                    fontSize={13}
                                    fontWeight={700}
                                >
                                    <FormattedMessage id="predicted" />
                                </Typography>
                            ) : (
                                dose.type
                            )}
                        </ValueText>
                    </LabelValueBox>
                    {dose.summationType && (
                        <LabelValueBox>
                            <LabelText>Dose Summation Type:</LabelText>
                            <ValueText>{dose.summationType}</ValueText>
                        </LabelValueBox>
                    )}
                    {doseGridResolution && (
                        <LabelValueBox>
                            <LabelText>Dose Grid Resolution (mm):</LabelText>
                            <ValueText>{doseGridResolution}</ValueText>
                        </LabelValueBox>
                    )}
                    {dose.planLabel && (
                        <LabelValueBox>
                            <LabelText>Description:</LabelText>
                            <ValueText>{dose.planLabel}</ValueText>
                        </LabelValueBox>
                    )}

                    {dose.maxDoseGy && (
                        <LabelValueBox>
                            <LabelText>Max Dose (Gy):</LabelText>
                            <ValueText>{dose.maxDoseGy.toFixed(1)}</ValueText>
                        </LabelValueBox>
                    )}

                    {dose.manufacturer && (
                        <LabelValueBox>
                            <LabelText>
                                <FormattedMessage id="manufacturer" />:
                            </LabelText>
                            <ValueText>{dose.manufacturer}</ValueText>
                        </LabelValueBox>
                    )}

                    {dose.manufacturerModelName && (
                        <LabelValueBox>
                            <LabelText>
                                <FormattedMessage id="manufacturerModelName" />:
                            </LabelText>
                            <ValueText>{dose.manufacturerModelName}</ValueText>
                        </LabelValueBox>
                    )}

                    {dose.seriesDescription && (
                        <LabelValueBox>
                            <LabelText>Series Description:</LabelText>
                            <ValueText>{dose.seriesDescription}</ValueText>
                        </LabelValueBox>
                    )}

                    {dose.doseComment && (
                        <LabelValueBox>
                            <LabelText>Dose Comment:</LabelText>
                            <ValueText>{dose.doseComment}</ValueText>
                        </LabelValueBox>
                    )}

                    {dose.softwareVersions && (
                        <LabelValueBox>
                            <LabelText>Software Versions:</LabelText>
                            <ValueText>{dose.softwareVersions}</ValueText>
                        </LabelValueBox>
                    )}

                    <LabelValueBox>
                        <LabelText>Normalization (Gy):</LabelText>
                        <ValueText>
                            {dose.normalizationValueGy.toFixed(1)}
                        </ValueText>
                    </LabelValueBox>

                    {doseScaleDisplay && (
                        <LabelValueBox>
                            <LabelText>Dose scaled by:</LabelText>
                            <ValueText>{doseScaleDisplay}</ValueText>
                        </LabelValueBox>
                    )}

                    <StyledIdField label="SOP Instance UID" id={dose.sopUid} />
                </Box>
            }
            placement="right-start"
        >
            <Info size={12} />
        </Tooltip>
    );
}
